import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { Icon } from "../icon/Icon";
import {
  dropdownContent,
  dropdownDetachedContent,
  dropdownItem,
  dropdownSubContent,
  dropdownTrigger,
  subTriggerChevron,
  triggerChevron
} from "./DropdownMenu.css";

export function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

export function DropdownMenuTrigger({
  children,
  className,
  asChild,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  // asChild: the child element IS the trigger — it keeps its own look, so
  // skip the select-style trigger class and the appended chevron.
  if (asChild) {
    return (
      <DropdownMenuPrimitive.Trigger
        asChild
        className={className}
        data-slot="dropdown-menu-trigger"
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Trigger>
    );
  }
  return (
    <DropdownMenuPrimitive.Trigger
      className={clsx(dropdownTrigger, className)}
      data-slot="dropdown-menu-trigger"
      {...props}
    >
      {children}
      <div className={triggerChevron}>
        <Icon name="chevron-down" />
      </div>
    </DropdownMenuPrimitive.Trigger>
  );
}

export function DropdownMenuContent({
  detached,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  /**
   * For triggers that aren't select-shaped (e.g. a tab): renders the panel in
   * a portal (escaping overflow-clipping ancestors), sized to its items
   * instead of the trigger width, with all corners rounded.
   */
  detached?: boolean;
}) {
  const content = (
    <DropdownMenuPrimitive.Content
      data-slot="dropdown-menu-content"
      style={{
        position: "relative"
      }}
      className={detached ? dropdownDetachedContent : dropdownContent}
      {...props}
    />
  );
  if (!detached) return content;
  return <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal>;
}

export function DropdownMenuItem({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={clsx(dropdownItem, className)}
      {...props}
    />
  );
}

export function DropdownMenuSub(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>
) {
  return <DropdownMenuPrimitive.Sub {...props} />;
}

export function DropdownMenuSubTrigger({
  children,
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger>) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      className={clsx(dropdownItem, className)}
      {...props}
    >
      {children}
      <div className={subTriggerChevron}>
        <Icon name="chevron-right" />
      </div>
    </DropdownMenuPrimitive.SubTrigger>
  );
}

export function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    // Portal is required: the parent menu panel clips its children
    // (overflow: hidden on dropdownContent), so an inline SubContent opens
    // invisibly. Portaling to <body> escapes the clip.
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        data-slot="dropdown-menu-sub-content"
        className={clsx(dropdownSubContent, className)}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}
